import { PERMISSIONS, type PermissionCode } from '@oxxean/types';
import type { IconName } from '../components/Icon';

/**
 * Los 19 módulos del menú.
 *
 * El menú se construye a partir de `user.permissions`: una entrada sin permiso NO
 * se renderiza, y su ruta además está protegida con `<RequirePermission>` y en el
 * endpoint. Tres capas, porque la primera es sólo comodidad.
 */
export type NavGroup = 'general' | 'indicadores' | 'gestion' | 'operacion' | 'administracion';

export const NAV_GROUP_LABEL: Record<NavGroup, string> = {
  general: 'General',
  indicadores: 'Indicadores',
  gestion: 'Gestión',
  operacion: 'Operación',
  administracion: 'Administración',
};

export interface NavItem {
  key: string;
  label: string;
  path: string;
  icon: IconName;
  group: NavGroup;
  /** `undefined` = visible para cualquier sesión autenticada (Inicio). */
  permission?: PermissionCode;
  description: string;
  /** ¿La barra de filtros globales aplica a esta pantalla? */
  showFilters: boolean;
}

export const NAV_ITEMS: NavItem[] = [
  {
    key: 'home',
    label: 'Inicio',
    path: '/',
    icon: 'home',
    group: 'general',
    description: 'Estado general de la plataforma y accesos directos.',
    showFilters: false,
  },
  {
    key: 'executive',
    label: 'Dashboard ejecutivo',
    path: '/dashboard-ejecutivo',
    icon: 'dashboard',
    group: 'general',
    permission: PERMISSIONS.DASHBOARD_EXECUTIVE_VIEW,
    description: 'Los 15 indicadores y los 12 gráficos consolidados del período.',
    showFilters: true,
  },
  {
    key: 'headcount',
    label: 'Dotación',
    path: '/dotacion',
    icon: 'people',
    group: 'indicadores',
    permission: PERMISSIONS.HEADCOUNT_VIEW,
    description: 'Dotación de inicio, cierre y promedio por empresa y área.',
    showFilters: true,
  },
  {
    key: 'movements',
    label: 'Ingresos y egresos',
    path: '/ingresos-egresos',
    icon: 'exchange',
    group: 'indicadores',
    permission: PERMISSIONS.HEADCOUNT_VIEW,
    description: 'Flujo de personas y su conciliación con contratos y finiquitos.',
    showFilters: true,
  },
  {
    key: 'turnover',
    label: 'Rotación',
    path: '/rotacion',
    icon: 'rotation',
    group: 'indicadores',
    permission: PERMISSIONS.TURNOVER_VIEW,
    description: 'Egresos sobre dotación promedio, calculado como razón de sumas.',
    showFilters: true,
  },
  {
    key: 'attendance',
    label: 'Asistencia',
    path: '/asistencia',
    icon: 'clock',
    group: 'indicadores',
    permission: PERMISSIONS.ATTENDANCE_VIEW,
    description: 'Marcaciones, días trabajados y calidad del registro.',
    showFilters: true,
  },
  {
    key: 'absence',
    label: 'Ausentismo',
    path: '/ausentismo',
    icon: 'absence',
    group: 'indicadores',
    permission: PERMISSIONS.ABSENCE_VIEW,
    description: 'Días de licencia y de ausencia sobre días laborales esperados.',
    showFilters: true,
  },
  {
    key: 'lateness',
    label: 'Atrasos',
    path: '/atrasos',
    icon: 'late',
    group: 'indicadores',
    permission: PERMISSIONS.LATENESS_VIEW,
    description: 'Atrasos registrados y minutos acumulados por ámbito.',
    showFilters: true,
  },
  {
    key: 'overtime',
    // Rótulo obligatorio: la plataforma informa horas APROBADAS, no pagadas.
    label: 'Horas extra',
    path: '/horas-extra',
    icon: 'overtime',
    group: 'indicadores',
    permission: PERMISSIONS.OVERTIME_VIEW,
    description: 'Horas extra aprobadas. La plataforma no dispone del dato de pago.',
    showFilters: true,
  },
  {
    key: 'vacation',
    label: 'Vacaciones',
    path: '/vacaciones',
    icon: 'vacation',
    group: 'indicadores',
    permission: PERMISSIONS.VACATION_VIEW,
    description: 'Días pendientes y trabajadores con más de dos períodos acumulados.',
    showFilters: true,
  },
  {
    key: 'documents',
    label: 'Documentación laboral',
    path: '/documentacion-laboral',
    icon: 'document',
    group: 'indicadores',
    permission: PERMISSIONS.DOCUMENTS_VIEW,
    description: 'Cumplimiento de contratos, anexos y finiquitos en plazo.',
    showFilters: true,
  },
  {
    key: 'labor-relations',
    label: 'Relaciones laborales',
    path: '/relaciones-laborales',
    icon: 'gavel',
    group: 'gestion',
    permission: PERMISSIONS.LABOR_RELATIONS_VIEW,
    description: 'Casos de relaciones laborales registrados en la plataforma.',
    showFilters: true,
  },
  {
    key: 'inspections',
    label: 'Fiscalizaciones',
    path: '/fiscalizaciones',
    icon: 'clipboard',
    group: 'gestion',
    permission: PERMISSIONS.INSPECTIONS_VIEW,
    description: 'Fiscalizaciones y observaciones de organismos externos.',
    showFilters: true,
  },
  {
    key: 'findings',
    label: 'Hallazgos',
    path: '/hallazgos',
    icon: 'alert',
    group: 'gestion',
    permission: PERMISSIONS.FINDINGS_VIEW,
    description: 'Hallazgos de gestión, su riesgo y las acciones comprometidas.',
    showFilters: true,
  },
  {
    key: 'diversity',
    label: 'Diversidad',
    path: '/diversidad',
    icon: 'user',
    group: 'indicadores',
    // Describe la composición de la dotación, así que comparte su permiso. Muestra
    // datos personales agregados —el género es categoría sensible— y el acceso queda
    // auditado en el servidor.
    permission: PERMISSIONS.HEADCOUNT_VIEW,
    description: 'Composición de la dotación por género, edad y nacionalidad.',
    showFilters: true,
  },
  {
    key: 'sync',
    label: 'Sincronizaciones',
    path: '/sincronizaciones',
    icon: 'sync',
    group: 'operacion',
    permission: PERMISSIONS.SYNC_VIEW,
    description: 'Ejecuciones con BUK, resultados y detalle de errores.',
    showFilters: false,
  },
  {
    key: 'shifts',
    label: 'Turnos BUK',
    path: '/turnos',
    icon: 'clock',
    group: 'operacion',
    // Mismo permiso que asistencia: el turno es el criterio con el que se calculan
    // los atrasos, y quien los lee debe poder consultarlo.
    permission: PERMISSIONS.ATTENDANCE_VIEW,
    description: 'Horario planificado por empresa y área, con su origen.',
    showFilters: false,
  },
  {
    key: 'unidentified',
    label: 'Marcaciones sin identificar',
    path: '/marcaciones-sin-identificar',
    icon: 'alert',
    group: 'operacion',
    // `ATTENDANCE_VIEW` y no `SYNC_VIEW`: la pantalla muestra RUT, que es dato
    // personal, y quien revisa el historial de sincronizaciones no necesariamente
    // debe verlos.
    permission: PERMISSIONS.ATTENDANCE_VIEW,
    description: 'Personas que marcan asistencia y no existen en BUK.',
    showFilters: false,
  },
  {
    key: 'users',
    label: 'Usuarios',
    path: '/usuarios',
    icon: 'user-cog',
    group: 'administracion',
    permission: PERMISSIONS.USERS_VIEW,
    description: 'Cuentas, roles asignados y ámbito de empresa y área.',
    showFilters: false,
  },
  {
    key: 'roles',
    label: 'Roles y permisos',
    path: '/roles-permisos',
    icon: 'shield',
    group: 'administracion',
    permission: PERMISSIONS.ROLES_VIEW,
    description: 'Composición de permisos de cada rol.',
    showFilters: false,
  },
  {
    key: 'audit',
    label: 'Auditoría',
    path: '/auditoria',
    icon: 'history',
    group: 'administracion',
    permission: PERMISSIONS.AUDIT_VIEW,
    description: 'Registro de toda mutación: quién, qué, cuándo y desde dónde.',
    showFilters: true,
  },
  {
    key: 'settings',
    label: 'Configuración',
    path: '/configuracion',
    icon: 'settings',
    group: 'administracion',
    permission: PERMISSIONS.SETTINGS_VIEW,
    description: 'Umbrales analíticos, integraciones y parámetros del período.',
    showFilters: false,
  },
];

export const NAV_GROUP_ORDER: NavGroup[] = [
  'general',
  'indicadores',
  'gestion',
  'operacion',
  'administracion',
];

export function findNavItem(pathname: string): NavItem | undefined {
  if (pathname === '/') return NAV_ITEMS[0];
  return NAV_ITEMS.find((item) => item.path !== '/' && pathname.startsWith(item.path));
}
